import { useLocale as useLocaleR42 } from '@/lib/locale';
// -----------------------------------------------------------------------------
// <SplashHeroMascot /> \u2014 the flying, talking landing-page tour guide.
// -----------------------------------------------------------------------------
//
// User-facing behavior:
//   1. On mount, the mascot waits for the logo animation to finish (default
//      ~1.6s), then flies in from off-screen-left.
//   2. Orbits the site logo once (a wide arc), then swoops toward the viewer
//      (scale-up + slight forward tilt), then parks to the right of the logo.
//   3. A pulsing "Start Tour" button appears. Click it and:
//        \u2022 Audio is unlocked (whoosh SFX + TTS).
//        \u2022 The mascot delivers his first line, speech bubble opens.
//        \u2022 When the line finishes, he says "Come with me" and flies to the
//          next section anchor \u2014 the page auto-scrolls to bring him into
//          view smoothly.
//        \u2022 Repeats for every stop in TOUR_STOPS.
//   4. At the end he shrinks to a corner "Meet Splash" mini-button that can
//      re-open the tour.
//
// Implementation notes:
//   * Positioning is done in FIXED viewport coordinates. When we fly to a
//     section anchor, we compute where that anchor SHOULD be relative to the
//     viewport at the moment we arrive \u2014 which means we also programmatically
//     scroll the section into view first, then park next to it.
//   * Framer Motion spring transitions carry us between waypoints. The orbit
//     uses a keyframes array (x[], y[]) so it traces a circle.
//   * Narration uses fixed Murph recordings via mascot-audio.js; effects use Web Audio.
//   * Everything is respectful of prefers-reduced-motion \u2014 we still show the
//     mascot but skip flight, snap to positions instantly.
//   * A "Skip" button always exists so the mascot never traps the user.
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Volume2, VolumeX, X, Play, SkipForward, ChevronDown } from 'lucide-react';
import { playWhoosh, playLandingImpact, playSparkle, playRecordedClip, stopRecordedClip, unlockAudio, preloadVoiceClips } from './mascot-audio';
import { TOUR_STOPS, INTRO_AUDIO, INTRO_LINE, OUTRO_AUDIO, OUTRO_LINE } from './mascot-script';
import { MascotFX } from './MascotFX';

// R38 motion tokens: shorter travel, gentle landing, and room to read.
const TOUR_MOTION = Object.freeze({
    scrollMs: 480,
    anticipationMs: 130,
    flightMinSeconds: 0.52,
    flightMaxSeconds: 0.98,
    flightPixelsPerSecond: 1400,
    landingMs: 120,
    betweenStopsMs: 200,
    arcMin: 44,
    arcMax: 150,
    arcRatio: 0.21,
    bankDegrees: 16,
    settleStiffness: 180,
    settleDamping: 24,
    introDelayMs: 1000,
    introOrbitSeconds: 1.7,
});

// Bound the page's motion to the same predictable beat as Splash. Native smooth
// scrolling varies with page distance; a fixed wait can leave the target moving.
function scrollTourAnchor(anchor, reducedMotion, isCurrentRun) {
    const start = window.scrollY;
    const rect = anchor.getBoundingClientRect();
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const target = Math.max(0, Math.min(maxScroll,
        start + rect.top + (rect.height - window.innerHeight) / 2));
    if (!isCurrentRun()) return Promise.resolve();
    if (reducedMotion || Math.abs(target - start) < 2) {
        window.scrollTo({ top: target, behavior: 'instant' });
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const began = performance.now();
        const tick = (now) => {
            if (!isCurrentRun()) { resolve(); return; }
            const progress = Math.min(1, (now - began) / TOUR_MOTION.scrollMs);
            window.scrollTo({ top: start + (target - start) * easeInOutSine(progress), behavior: 'instant' });
            if (progress < 1) requestAnimationFrame(tick);
            else resolve();
        };
        requestAnimationFrame(tick);
    });
}

// ---- Phase 0br easing + arc-baking helpers ---------------------------------
// The pack replaces smooth-eased Framer transitions with LINEAR playback of
// keyframe arrays that already have easing baked into their sample spacing.
// This lets us also bake in per-sample rotation (banking) and per-sample
// scale (z-depth) using the same keyframe channel — impossible with a
// Framer easing curve alone.
function easeInOutSine(t) {
    return -(Math.cos(Math.PI * t) - 1) / 2;
}

/** Compute a per-sample rotation array so the mascot BANKS into turns —
 *  looking down when diving, up when rising, tilting into curves like a
 *  bird of prey. Bounded to [-maxDeg, maxDeg] and eased-out at the tail
 *  so he settles level for landing. */
function bankFromPath(xs, ys, maxDeg = 22) {
    const n = xs.length - 1;
    const rot = [];
    for (let i = 0; i <= n; i++) {
        const i0 = Math.max(0, i - 1);
        const i1 = Math.min(n, i + 1);
        const dx = xs[i1] - xs[i0];
        const dy = ys[i1] - ys[i0];
        if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) { rot.push(0); continue; }
        let deg = Math.atan2(dy, Math.abs(dx)) * (180 / Math.PI) * 0.5;
        deg *= dx >= 0 ? 1 : -1;
        rot.push(Math.max(-maxDeg, Math.min(maxDeg, deg)));
    }
    for (let i = Math.max(0, n - 4); i <= n; i++) rot[i] *= (n - i) / 5;
    return rot;
}

/** Build a smooth arc from start to end passing through an apex above
 *  the midpoint. Returns {x[], y[], rot[]} sample arrays with easing
 *  BAKED into sample spacing (so we play back linearly). */
function buildArcPath(start, end, apexLift, samples = 36) {
    const mx = (start.x + end.x) / 2;
    const my = Math.min(start.y, end.y) - apexLift;
    const xs = [];
    const ys = [];
    for (let i = 0; i <= samples; i++) {
        const te = easeInOutSine(i / samples);   // one continuous glide, no hard mid-flight beats
        const a = 1 - te;
        xs.push(a * a * start.x + 2 * a * te * mx + te * te * end.x);
        ys.push(a * a * start.y + 2 * a * te * my + te * te * end.y);
    }
    return { x: xs, y: ys, rot: bankFromPath(xs, ys, TOUR_MOTION.bankDegrees) };
}

/** Fixed-viewport coordinates in pixels. All position keyframes are in this
 *  coordinate space so the mascot is a `position: fixed` element that flies
 *  independently of scroll.
 *
 *  Mobile scale-down: on narrow viewports (<768px) we shrink the mascot to
 *  roughly 60% of desktop size so he doesn't dominate the screen, and pin
 *  him to a bottom-corner "floating helper" position instead of trying to
 *  fit next to each section's content (there's no horizontal room on a
 *  390px screen). */
const DESKTOP_W = 180;
const DESKTOP_H = 220;
const MOBILE_W = 110;
const MOBILE_H = 135;

function isMobileViewport() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
}

function mascotDims() {
    return isMobileViewport()
        ? { w: MOBILE_W, h: MOBILE_H }
        : { w: DESKTOP_W, h: DESKTOP_H };
}

/** Get the current bounding box of a DOM element by id, in viewport coords.
 *  Returns null if the element is not (yet) in the DOM. */
function getRect(id) {
    if (typeof window === 'undefined') return null;
    const el = document.getElementById(id);
    if (!el) return null;
    return el.getBoundingClientRect();
}

/** Compute the target viewport position for the mascot when parked next to
 *  a section anchor. `align='right'` puts him hovering off the section's
 *  right edge; `'left'` on the left; `'center'` above center. Never lets
 *  him leave the viewport bounds. */
function parkPositionForAnchor(rect, align, viewportW, viewportH) {
    const { w: MW, h: MH } = mascotDims();
    const mobile = isMobileViewport();
    if (!rect) {
        // Fallback: park bottom-right of viewport.
        return { x: viewportW - MW - 16, y: viewportH - MH - 16 };
    }
    // On mobile we ALWAYS park at the bottom-right of the viewport (like a
    // floating helper). This keeps the mascot from covering the section
    // content, and the speech bubble opens above him.
    if (mobile) {
        return { x: viewportW - MW - 12, y: viewportH - MH - 16 };
    }
    // Desktop: align vertically with the section's vertical center.
    let y = rect.top + rect.height / 2 - MH / 2;
    // Clamp so he never goes above the top nav (approx 64px) or below.
    y = Math.max(72, Math.min(y, viewportH - MH - 24));

    let x;
    if (align === 'left') {
        x = Math.max(24, rect.left - MW - 8);
    } else if (align === 'center') {
        x = rect.left + rect.width / 2 - MW / 2;
    } else {
        x = Math.min(viewportW - MW - 24, rect.right + 8);
    }
    // If left-align would push him off-screen (narrow sections on mobile),
    // fall back to right-align.
    if (x < 24) x = Math.min(viewportW - MW - 24, rect.right + 8);
    if (x + MW > viewportW) x = viewportW - MW - 24;
    return { x, y };
}

export function SplashHeroMascot({ enabled = true }) {
    const { t: t42 } = useLocaleR42();
    const prefersReducedMotion = useReducedMotion();

    // Phase machine \u2014 drives which animation runs.
    //   'boot'    \u2014 waiting for logo animation to finish before we enter
    //   'flyin'   \u2014 flying in from left
    //   'orbit'   \u2014 orbiting around the logo
    //   'zoom'    \u2014 zooming toward viewer briefly
    //   'parked'  \u2014 hovering next to logo, "Start Tour" pulsing
    //   'touring' \u2014 executing sequential TOUR_STOPS
    //   'done'    \u2014 tour finished; mini bubble in corner
    //   'skipped' \u2014 user hit X; only mini bubble in corner
    const [phase, setPhase] = useState('boot');
    const [stopIndex, setStopIndex] = useState(-1); // -1 means intro line, 0..N stops
    const [showBubble, setShowBubble] = useState(false);
    const [muted, setMuted] = useState(false);
    const [audioError, setAudioError] = useState(false);
    const tourRunRef = useRef(0);
    const audioOptInRef = useRef(false);
    const [position, setPosition] = useState(() => {
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
        const { w: MW } = mascotDims();
        return { x: -MW - 40, y: vh * 0.35 };
    });
    // 'right' means he's facing right (flip when moving left).
    const [facing, setFacing] = useState('right');
    // Rotation degrees \u2014 leans forward when flying, level when parked.
    const [tilt, setTilt] = useState(0);
    const [scale, setScale] = useState(0.9);
    // Flight-path keyframes. When non-null, drives the mascot's animate
    // prop with an ARRAY of x/y values so Framer Motion interpolates
    // smoothly across the whole path in a single tween — no stop/start
    // between waypoints. When null, we're stationary and the normal
    // spring-to-position code path runs.
    //
    // Shape: { x: number[], y: number[], duration: seconds, ease }
    const [flightPath, setFlightPath] = useState(null);
    // Squash-and-stretch. stretchX > 1 elongates horizontally (flying),
    // stretchY > 1 elongates vertically (jump anticipation / bounce).
    const [stretchX, setStretchX] = useState(1);
    const [stretchY, setStretchY] = useState(1);
    // Which pose PNG is showing. 'stand' (hands on hips), 'fly' (Superman
    // horizontal), or 'point' (arm extended). The single biggest visual
    // upgrade over a rotating single-sprite mascot is having distinct
    // source poses that snap-swap at the right beat of the flight.
    const [pose, setPose] = useState('stand');
    // Water-splash burst events. Each entry is { id, x, y }. Rendered by
    // <WaterSplash /> children; they self-remove after their animation ends.
    const [splashes, setSplashes] = useState([]);
    // Sparkle particles (cyan/gold pixie dust) — spawned continuously during
    // flight for that Peter-Pan magic-trail vibe.
    const [sparkles, setSparkles] = useState([]);
    // Screen-shake offset applied to the entire mascot root at landing.
    const [shake, setShake] = useState({ x: 0, y: 0 });
    // Head-bob offset — subtle vertical oscillation of the sprite while
    // speaking. Read by the img's style so it doesn't fight Framer's
    // absolute position animation on the parent.
    const [headBob, setHeadBob] = useState(0);
    const [showLoveMuteBar, setShowLoveMuteBar] = useState(true);
    // Phase 0b5 — "Superman flies behind the Earth" effect. During the
    // top-arc of the intro orbit around the "S" logo (angle π → 2π),
    // we lower the mascot's z-index so he visibly dips BEHIND the logo,
    // then swings back in front on the bottom-arc (angle 2π → 3π).
    // The logo container in Landing.jsx sits at z-30 with an isolated
    // stacking context; we toggle the mascot root between z-[45] (front)
    // and z-[20] (behind).
    const [behindLogo, setBehindLogo] = useState(false);

    // We keep the raw current line + CTA in state so the bubble text is
    // controlled from the tour driver below.
    const [bubbleText, setBubbleText] = useState('');
    const [bubbleCta, setBubbleCta] = useState('');

    const cancelledRef = useRef(false);
    const runningRef = useRef(false);
    const splashIdRef = useRef(0);
    // Ref-backed mirror of `position` so async loops can read the latest
    // value without stale closures (setState is batched).
    const positionRef = useRef(position);
    useEffect(() => { positionRef.current = position; }, [position]);
    const poseRef = useRef(pose);
    useEffect(() => { poseRef.current = pose; }, [pose]);
    const facingRef = useRef(facing);
    useEffect(() => { facingRef.current = facing; }, [facing]);
    // Ref-backed mirror of `muted`. The tour driver (runTour) is a single
    // long-running async function — it captures `muted` from the render
    // closure at the moment the tour STARTS, so reading the state variable
    // inside the loop sees a stale value forever. Reading the ref instead
    // means a mid-tour mute click takes effect on the very next sound.
    const mutedRef = useRef(muted);
    useEffect(() => { mutedRef.current = muted; }, [muted]);

    // Toggle mute. When muting we must ALSO kill whatever is playing right
    // now (recorded MP3 clip or browser TTS) — otherwise the current line
    // keeps talking and the button looks broken. The ref is updated
    // synchronously so the async tour loop sees the new value immediately,
    // without waiting a render.
    const toggleMute = useCallback(() => {
        const next = !mutedRef.current;
        mutedRef.current = next;
        setMuted(next);
        if (next) {
            stopRecordedClip();
        }
    }, []);

    // ----- animation helper: (motion-blur trail removed in Phase 0br) ------
    // Motion-trail is now rendered by <MascotFX /> as a canvas overlay with
    // depth-sorted volumetric puffs + dynamic shadows. See MascotFX.jsx.

    // ----- animation helper: fire a water-splash burst at (x, y) ----------
    const emitSplash = useCallback((x, y, small = false) => {
        const s = { id: ++splashIdRef.current, x, y, small };
        setSplashes((prev) => [...prev, s]);
    }, []);

    // ----- sparkle particles ----------------------------------------------
    // Spawn N pixie-dust particles at (x, y) that arc outward and fade.
    // Cheap: pure setState + Framer Motion, no canvas.
    const sparkleIdRef = useRef(0);
    const emitSparkles = useCallback((x, y, count = 4) => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = 20 + Math.random() * 40;
            const dx = Math.cos(angle) * dist;
            const dy = Math.sin(angle) * dist - 10;
            arr.push({
                id: ++sparkleIdRef.current,
                x, y,
                dx, dy,
                size: 4 + Math.random() * 5,
                color: Math.random() > 0.5 ? '#00d4ff' : '#ffd700',
                life: 750 + Math.random() * 350,
            });
        }
        setSparkles((prev) => [...prev.slice(-24), ...arr]);
        // Auto-clean.
        setTimeout(() => {
            const ids = new Set(arr.map((s) => s.id));
            setSparkles((prev) => prev.filter((s) => !ids.has(s.id)));
        }, 1200);
    }, []);

    // ----- screen shake ---------------------------------------------------
    // Trigger a brief translation of the mascot root to simulate impact.
    // Small enough that it doesn't disorient, big enough that it registers.
    const triggerShake = useCallback((intensity = 6) => {
        if (prefersReducedMotion) return;
        let n = 0;
        const iv = setInterval(() => {
            n += 1;
            if (n > 6) {
                clearInterval(iv);
                setShake({ x: 0, y: 0 });
                return;
            }
            const decay = 1 - n / 7;
            setShake({
                x: (Math.random() - 0.5) * intensity * decay * 2,
                y: (Math.random() - 0.5) * intensity * decay * 2,
            });
        }, 35);
    }, [prefersReducedMotion]);


    // ----- viewport helpers -----------------------------------------------
    const getVW = () => (typeof window !== 'undefined' ? window.innerWidth : 1200);
    const getVH = () => (typeof window !== 'undefined' ? window.innerHeight : 800);

    // ----- boot sequence ---------------------------------------------------
    // Warm the fixed Murph recordings without playback, and give the site
    // logo animation time to land. Only Start Tour opts into audible sound.
    useEffect(() => {
        if (!enabled) return;
        // Reset cancelled on every mount. Under React 18 StrictMode this
        // effect fires twice in dev — without this reset, the cleanup from
        // the first invocation sets cancelledRef=true which then makes the
        // second invocation's startIntro bail out immediately.
        cancelledRef.current = false;
        // Warm the voice-clip blob cache while Splash flies in. Doing this
        // ahead of the Start-Tour click means playRecordedClip() later
        // resolves from cache in a microtask — no network await between the
        // user's click and audio.play(), which is what Safari/iOS require
        // for the gesture token to survive (fixes "Splash has no voice").
        // requestIdleCallback keeps it off the critical rendering path.
        const preload = () => preloadVoiceClips([INTRO_AUDIO, ...TOUR_STOPS.map((s) => s.audio), OUTRO_AUDIO]);
        let idleId = null;
        let preloadTimer = null;
        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(preload, { timeout: 4000 });
        } else {
            preloadTimer = setTimeout(preload, 2000);
        }
        const t = setTimeout(() => {
            if (!cancelledRef.current) startIntro();
        }, prefersReducedMotion ? 300 : TOUR_MOTION.introDelayMs);
        return () => {
            clearTimeout(t);
            if (preloadTimer) clearTimeout(preloadTimer);
            if (idleId !== null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleId);
            }
            cancelledRef.current = true;
            tourRunRef.current += 1;
            audioOptInRef.current = false;
            stopRecordedClip();
        };
    }, [enabled]);

    // ----- intro: fly in, orbit logo, zoom to viewer, park -----------------
    async function startIntro() {
        if (runningRef.current) return;
        runningRef.current = true;

        const logoRect = getRect('mascot-anchor-logo') || getRect('mascot-anchor-hero');
        const vw = getVW();
        const vh = getVH();

        // Reduced motion: snap into parked position, skip animations.
        if (prefersReducedMotion) {
            const anchor = getRect('mascot-anchor-hero');
            const parked = parkPositionForAnchor(anchor, 'right', vw, vh);
            setPosition(parked);
            setTilt(0);
            setScale(1);
            setPhase('parked');
            setShowBubble(true);
            setBubbleText("Hi, I'm Splash! Click Start Tour and I'll show you around.");
            setBubbleCta('Start Tour');
            runningRef.current = false;
            return;
        }

        setPhase('flyin');
        setFacing('right');
        setPose('fly'); // Superman-pose sprite for the entrance!
        setStretchX(1.15); setStretchY(0.9); // stretched in flight direction

        // Fly in from off-screen-left toward a point just left of the logo.
        const logoCx = (logoRect?.left ?? vw / 2) + (logoRect?.width ?? 0) / 2;
        const logoCy = (logoRect?.top ?? vh * 0.25) + (logoRect?.height ?? 0) / 2;
        const flyInX = Math.max(40, logoCx - 320);
        const flyInY = logoCy - mascotDims().h / 2;
        setTilt(0); // flying sprite already has forward tilt built into art
        setScale(0.95);
        // Try to play a whoosh once audio is unlocked (may not fire if user
        // hasn't interacted yet; that's fine \u2014 whooshes will kick in from
        // "Start Tour" onward).
        if (audioOptInRef.current && !mutedRef.current) playWhoosh({ direction: 'up', duration: 0.55 });
        setPosition({ x: flyInX, y: flyInY });
        // (Phase 0br) Motion-blur is now rendered by MascotFX canvas overlay —
        // no per-step ghost emission needed. The canvas engine reads the
        // mascot's live DOM rect every frame and emits volumetric puffs
        // proportional to real velocity, so it "just works" over any
        // animation path.
        await wait(prefersReducedMotion ? 100 : 340);

        // Orbit around the logo — computed as a single continuous keyframe
        // path so Framer Motion tweens the ENTIRE arc smoothly. The old
        // approach re-set position 12 times which interrupted each spring
        // mid-flight and produced the stop-and-go choppiness.
        setPhase('orbit');
        setTilt(0);
        setScale(0.9);
        const radiusMax = isMobileViewport() ? 110 : 260;
        const radiusMin = isMobileViewport() ? 70 : 140;
        const radius = Math.min(radiusMax, Math.max(radiusMin, (logoRect?.width ?? 300) * 0.55));
        const cx = logoCx - mascotDims().w / 2;
        const cy = logoCy - mascotDims().h / 2;
        const waypoints = 48;
        const orbitX = [];
        const orbitY = [];
        const orbitS = [];
        for (let i = 0; i <= waypoints; i++) {
            // Bake ease-in-out into sample SPACING so playback is linear
            // (see easing helpers at top of file). Waypoints along a
            // clockwise sweep, plus a per-sample z-depth scale that dips
            // when passing behind the logo apex.
            const pe = easeInOutSine(i / waypoints);
            const angle = Math.PI + pe * Math.PI * 2;
            orbitX.push(cx + Math.cos(angle) * radius);
            orbitY.push(cy + Math.sin(angle) * radius * 0.55);
            orbitS.push(0.92 - Math.sin(pe * Math.PI * 2) * 0.13);
        }
        const orbitDurationS = TOUR_MOTION.introOrbitSeconds;
        setFlightPath({
            x: orbitX,
            y: orbitY,
            s: orbitS,
            rot: bankFromPath(orbitX, orbitY, 16),
            duration: orbitDurationS,
        });
        // Spawn ghost trails + facing changes across the orbit while it's
        // animating. We can't listen to per-frame updates cheaply, so we
        // schedule these on a plain timer that matches the tween cadence.
        const facingChanges = [
            { at: 0.2, facing: 'right' },
            { at: 0.4, facing: 'right' },
            { at: 0.55, facing: 'left' },
            { at: 0.85, facing: 'left' },
        ];
        for (const change of facingChanges) {
            setTimeout(() => {
                if (!cancelledRef.current) setFacing(change.facing);
            }, change.at * orbitDurationS * 1000);
        }
        // Sparkle bursts across the orbit (motion-trail is now MascotFX canvas).
        for (let i = 0; i < 4; i++) {
            setTimeout(() => {
                if (cancelledRef.current) return;
                const cx2 = positionRef.current.x + mascotDims().w * 0.4;
                const cy2 = positionRef.current.y + mascotDims().h * 0.5;
                emitSparkles(cx2, cy2, 3);
            }, (i + 1) * (orbitDurationS * 1000) / 5);
        }
        // Superman-around-Earth z-index dance. The mascot's orbit sweeps
        // ONE clockwise revolution starting at angle=π (left of logo).
        //   • First half (angle π → 2π): mascot arcs OVER the top of the
        //     logo. In our slightly-squished isometric ellipse view, this
        //     visually reads as the "far side" of the orbit — so we drop
        //     him BEHIND the logo (z-[20], under logo's z-30).
        //   • Second half (angle 2π → 3π): mascot arcs UNDER the bottom
        //     of the logo — the "near side" — so he swings back IN FRONT
        //     (z-[45]).
        // Timing is 100% deterministic off the orbit tween's duration, so
        // the crossover is frame-accurate regardless of frame rate.
        setTimeout(() => { if (!cancelledRef.current) setBehindLogo(true); }, 30);
        setTimeout(() => { if (!cancelledRef.current) setBehindLogo(false); }, Math.round(orbitDurationS * 1000 * 0.5));
        await wait(prefersReducedMotion ? 40 : Math.round(orbitDurationS * 1000));
        // Commit final orbit position and clear the keyframe path so the
        // next state change reverts to spring behavior.
        setPosition({ x: orbitX[orbitX.length - 1], y: orbitY[orbitY.length - 1] });
        setFlightPath(null);
        // Safety: guarantee we're in front when orbit ends (in case timing
        // slipped or the user cancelled mid-orbit).
        setBehindLogo(false);

        // Zoom toward viewer: quick pop of scale + slight forward tilt.
        // Switch to the standing pose right before the zoom hits the viewer
        // so the freeze-frame reads as "hero has arrived, hands on hips."
        setPhase('zoom');
        setFacing('right');
        setTilt(0);
        setStretchX(1); setStretchY(1);
        setPose('stand');
        setScale(1.25);
        await wait(prefersReducedMotion ? 60 : 380);
        setScale(1);

        // Park to the right of the logo, with a landing water-splash
        // sparked at his feet to sell the impact.
        const parked = parkPositionForAnchor(logoRect, 'right', vw, vh);
        setPosition(parked);
        // Landing effects: splash + subtle screen shake + audio impact.
        emitSplash(parked.x + mascotDims().w / 2, parked.y + mascotDims().h - 6, false);
        if (audioOptInRef.current && !mutedRef.current) playLandingImpact({ volume: 0.28 });
        triggerShake(6);
        // Sparkle burst from where he lands.
        emitSparkles(parked.x + mascotDims().w / 2, parked.y + mascotDims().h * 0.6, 8);
        // Landing squash: brief compress, then spring back.
        setStretchX(1.15); setStretchY(0.85);
        await wait(160);
        setStretchX(1); setStretchY(1);
        setPhase('parked');
        setShowBubble(true);
        setBubbleText("Hi, I'm Splash! Click Start Tour and I'll show you around.");
        setBubbleCta('Start Tour');
        runningRef.current = false;
    }

    // ----- run the tour ---------------------------------------------------
    async function runTour() {
        if (runningRef.current) return;
        runningRef.current = true;
        cancelledRef.current = false;
        const runId = ++tourRunRef.current;
        const isCurrentRun = () => !cancelledRef.current && tourRunRef.current === runId;
        audioOptInRef.current = true;
        setAudioError(false);

        // Murph only. Failed/blocked audio keeps the matching text readable,
        // rather than silently changing Splash into a device/browser voice.
        const narrate = async (text, url) => {
            if (!isCurrentRun()) return;
            const readingTime = Math.max(1800, text.length * 45);
            setAudioError(false);
            if (mutedRef.current) { await wait(readingTime); return; }
            try {
                const outcome = await playRecordedClip(url, { volume: 1 });
                if (outcome === 'cancelled' && isCurrentRun() && mutedRef.current) await wait(readingTime);
            } catch {
                if (!isCurrentRun() || mutedRef.current) return;
                setAudioError(true);
                await wait(readingTime);
            }
        };

        // Enter the touring phase IMMEDIATELY on Start Tour, before any
        // audio unlock or intro clip. Two important reasons:
        //   1. The click-outside handler (scoped to 'skipped_open') would
        //      otherwise fire during the intro clip and cancel the tour on
        //      the very next tap.
        //   2. The root z-index policy jumps to z-[9999] in touring so
        //      Splash is guaranteed to sit above map widgets etc. from
        //      the FIRST frame of the tour \u2014 not after intro finishes.
        setPhase('touring');
        // Keep the same Mute/Skip/Close controls available during the intro.
        setStopIndex(-1);
        setBubbleText(INTRO_LINE);
        setBubbleCta('');
        setShowBubble(true);
        await unlockAudio();
        if (!isCurrentRun()) return;
        setPose('point');
        await narrate(INTRO_LINE, INTRO_AUDIO);
        if (!isCurrentRun()) return;
        setPose('stand');

        for (let i = 0; i < TOUR_STOPS.length; i++) {
            if (!isCurrentRun()) break;
            const stop = TOUR_STOPS[i];

            // Finish a bounded, cancellable page glide before measuring arrival.
            const anchor = document.getElementById(stop.anchorId);
            if (anchor) await scrollTourAnchor(anchor, prefersReducedMotion, isCurrentRun);
            if (!isCurrentRun()) break;

            // A short anticipation connects the page glide and banked flight.
            const rect = getRect(stop.anchorId);
            const target = parkPositionForAnchor(rect, stop.align, getVW(), getVH());
            const dx = target.x - positionRef.current.x;
            const dy = target.y - positionRef.current.y;
            const flyingDown = dy > 60;
            const going = dx >= 0 ? 'right' : 'left';
            setFacing(going);

            // Anticipation: brief compress + pull opposite of flight vector.
            setPose('stand');
            setStretchX(1); setStretchY(0.94);
            const antiPull = { x: positionRef.current.x - Math.sign(dx || 1) * 6,
                               y: positionRef.current.y + 4 };
            setPosition(antiPull);
            await wait(prefersReducedMotion ? 0 : TOUR_MOTION.anticipationMs);
            if (!isCurrentRun()) break;

            // Launch: swap to flying pose, stretch in flight direction.
            setPose('fly');
            setStretchX(1.1); setStretchY(0.95);
            setTilt(flyingDown ? 5 : (dx >= 0 ? 3 : -3));
            setScale(0.97);
            if (audioOptInRef.current && !mutedRef.current) playWhoosh({ direction: flyingDown ? 'down' : 'up', duration: 0.4 });
            setShowBubble(false);

            // Compute flight distance so we can scale splash + shake.
            const flightDist = Math.hypot(dx, dy);
            // Arc path: instead of a straight-line spring, we sweep him
            // through a mid-air apex point so the flight looks like a
            // hero jump rather than a slot-car track. Phase 0br: use the
            // new buildArcPath helper which bakes ease-in-out into sample
            // spacing AND generates per-sample banking rotation, so the
            // mascot tilts naturally into his turns.
            const startPos = positionRef.current;
            const apexLift = Math.max(TOUR_MOTION.arcMin, Math.min(TOUR_MOTION.arcMax, flightDist * TOUR_MOTION.arcRatio));
            const arcDurationS = Math.max(TOUR_MOTION.flightMinSeconds,
                Math.min(TOUR_MOTION.flightMaxSeconds, flightDist / TOUR_MOTION.flightPixelsPerSecond));
            if (!prefersReducedMotion) {
                setFlightPath({ ...buildArcPath(startPos, target, apexLift), duration: arcDurationS });
            }
            // Squash-and-stretch speed profile: relax toward neutral mid-cruise.
            if (!prefersReducedMotion) setTimeout(() => {
                if (isCurrentRun()) { setStretchX(1.04); setStretchY(0.98); }
            }, arcDurationS * 520);
            // Pixie-dust bursts across the arc (motion-trail is MascotFX canvas).
            const bursts = flightDist > 500 ? 4 : (flightDist > 250 ? 3 : 2);
            for (let g = 0; g < bursts; g++) {
                setTimeout(() => {
                    if (!isCurrentRun()) return;
                    const cx2 = positionRef.current.x + mascotDims().w * 0.4;
                    const cy2 = positionRef.current.y + mascotDims().h * 0.5;
                    emitSparkles(cx2, cy2, 3);
                }, (g + 1) * (arcDurationS * 1000) / (bursts + 1));
            }
            if (audioOptInRef.current && !mutedRef.current) playSparkle({ volume: 0.09 });
            await wait(prefersReducedMotion ? 60 : Math.round(arcDurationS * 1000));
            if (!isCurrentRun()) break;
            // Commit final position + clear the keyframe path so the
            // upcoming spring-back squash uses spring dynamics.
            setPosition(target);
            setFlightPath(null);
            if (!isCurrentRun()) break;

            // 3) Landing: water splash + squash-then-settle + pose swap.
            //    Splash intensity + screen shake scale with flight distance.
            const bigLanding = flightDist > 350;
            emitSplash(target.x + mascotDims().w / 2, target.y + mascotDims().h - 6, !bigLanding);
            if (audioOptInRef.current && !mutedRef.current) playLandingImpact({ volume: bigLanding ? 0.32 : 0.22 });
            if (bigLanding) triggerShake(4);
            else triggerShake(2);
            setPose('stand');
            setStretchX(1.08); setStretchY(0.94);
            setTilt(0);
            setScale(1.02);
            await wait(prefersReducedMotion ? 0 : TOUR_MOTION.landingMs);
            if (!isCurrentRun()) break;
            setStretchX(1); setStretchY(1);
            setScale(1);
            // Bubble opens on landing. For stops that are explaining a
            // feature we swap briefly to the pointing pose during the
            // speech — the arm-out gesture reads as "look here!"
            if (stop.align === 'left' || stop.align === 'right') {
                // Point in the direction of the section content, which is
                // opposite of Splash's park side.
                setFacing(stop.align === 'left' ? 'right' : 'left');
            }
            setPose('point');
            // Commit the visible stop and its complete text together on arrival.
            setStopIndex(i);
            setBubbleText(stop.line);
            setBubbleCta(stop.cta || '');
            setShowBubble(true);
            // Head-bob during speech: a slow gentle sine at ~1Hz that
            // starts when he begins talking and stops on cleanup. Signals
            // that he's the one talking, adds life to what would otherwise
            // be a static sprite during the read.
            let bobInterval = null;
            const bobStart = performance.now();
            bobInterval = setInterval(() => {
                if (!isCurrentRun()) { clearInterval(bobInterval); return; }
                const t = (performance.now() - bobStart) / 1000;
                setHeadBob(Math.sin(t * 4.2) * 3);
            }, 40);
            try {
                await narrate(stop.line, stop.audio);
            } finally {
                clearInterval(bobInterval);
                if (isCurrentRun()) setHeadBob(0);
            }
            if (!isCurrentRun()) break;
            // Return to standing for the beat before next flight.
            setPose('stand');

            // Short beat before flying to next.
            await wait(prefersReducedMotion ? 80 : TOUR_MOTION.betweenStopsMs);
        }

        // The final trial button and existing audio controls stay available
        // while Murph delivers the wrap-up at the pricing section.
        if (isCurrentRun()) {
            setPhase('done');
            setPose('wave');
            emitSparkles(positionRef.current.x + mascotDims().w / 2,
                         positionRef.current.y + mascotDims().h * 0.5, 10);
            setBubbleText(OUTRO_LINE);
            setBubbleCta('Start my trial');
            setShowBubble(true);
            await narrate(OUTRO_LINE, OUTRO_AUDIO);
        }
        if (tourRunRef.current === runId) runningRef.current = false;
    }

    // ----- skip / close ---------------------------------------------------
    // handleSkip() — user dismissed the current bubble (X button or click-outside).
    // Splash retreats to the bottom-right corner FULLY VISIBLE (no scale-shrink),
    // still animated (idle bob), and clickable to bring the tour back.
    // Design note: we intentionally reset stopIndex to -1 so that clicking Splash
    // again shows the "Start Tour" button — not the mid-tour chip that has no
    // click handler.
    const handleSkip = useCallback(() => {
        cancelledRef.current = true;
        tourRunRef.current += 1;
        runningRef.current = false;
        audioOptInRef.current = false;
        stopRecordedClip();
        setAudioError(false);
        setHeadBob(0);
        setShowBubble(false);
        setPhase('skipped');
        // Restore normal z-index if user hit close mid-orbit while the
        // "behind logo" phase is active.
        setBehindLogo(false);
        // Reset tour progression so the reopened bubble shows Start Tour again.
        setStopIndex(-1);
        // Cancel any in-flight arc/orbit tween — we want Splash to spring to
        // the corner cleanly, not continue whatever path he was on.
        setFlightPath(null);
        // Reset visual state to a clean standing pose. The corner position
        // itself is applied by the useEffect below (which also recomputes on
        // resize) so we don't duplicate the math here.
        setScale(1);
        setTilt(0);
        setStretchX(1);
        setStretchY(1);
        setPose('stand');
        setFacing('right');
    }, []);

    // handleReopen() — user clicked corner-Splash. Show the intro bubble with
    // the Start Tour button, WITHOUT moving Splash away from the corner. This
    // matches the requested UX: "we wanna see his whole body at all times in
    // the bottom right-hand corner."
    const handleReopen = useCallback(() => {
        tourRunRef.current += 1;
        runningRef.current = false;
        audioOptInRef.current = false;
        stopRecordedClip();
        setAudioError(false);
        cancelledRef.current = false;
        // Guarantee the Start-Tour button renders even if the user had
        // previously dismissed mid-tour (stopIndex may have advanced).
        setStopIndex(-1);
        setPhase('skipped_open');
        setShowBubble(true);
        setBubbleText("Hi, I'm Splash! Click Start Tour and I'll show you around.");
        setBubbleCta('Start Tour');
    }, []);

    // Handle click on the CTA chip depending on current phase.
    // 'parked'        — initial state next to the S logo after the intro flyin.
    // 'skipped_open'  — user re-opened the bubble by clicking corner-Splash.
    // Both should launch the tour when Start Tour is pressed.
    const handleBubbleCta = useCallback(() => {
        if ((phase === 'parked' || phase === 'skipped_open') && stopIndex < 0) {
            runTour();
        } else if (phase === 'done') {
            // Preserve the existing pricing CTA, but never talk over its action.
            tourRunRef.current += 1;
            runningRef.current = false;
            audioOptInRef.current = false;
            stopRecordedClip();
            setAudioError(false);
            const el = document.getElementById('mascot-anchor-pricing');
            el?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        } else if (phase === 'touring') {
            // Chip during touring is a nudge to keep scrolling; no-op click.
        }
    }, [phase, stopIndex, prefersReducedMotion]);

    // Whenever Splash enters (or is already in) a corner phase, snap him to
    // the bottom-right position, fully visible, at 1.0 scale. Running as an
    // effect means the corner position is always recomputed on phase change
    // and (via the resize listener below) on viewport resize.
    useEffect(() => {
        if (phase !== 'skipped' && phase !== 'skipped_open') return;
        const { w: MW, h: MH } = mascotDims();
        // Bottom-right, with margin. FULL body visible — not scaled down.
        setPosition({ x: getVW() - MW - 24, y: getVH() - MH - 24 });
    }, [phase]);

    // Click-outside handler for the re-opened bubble. Per the requested UX,
    // "Clicking anywhere off of Splash parks him" — so any pointerdown
    // outside the mascot figure OR the speech bubble collapses back to
    // the corner-standby ('skipped') state without cancelling anything.
    useEffect(() => {
        if (phase !== 'skipped_open') return;
        const handler = (e) => {
            const figure = document.querySelector('[data-testid="splash-mascot-figure"]');
            const bubble = document.querySelector('[data-testid="splash-mascot-bubble"]');
            if (figure && figure.contains(e.target)) return;
            if (bubble && bubble.contains(e.target)) return;
            // Click was truly outside — collapse the bubble but keep Splash
            // parked in the corner.
            setShowBubble(false);
            setPhase('skipped');
        };
        // Defer attach by one tick so the very click that entered
        // 'skipped_open' (via the mini or the mascot itself) doesn't
        // immediately trigger the close on the same event loop turn.
        const t = setTimeout(() => {
            document.addEventListener('mousedown', handler);
            document.addEventListener('touchstart', handler, { passive: true });
        }, 50);
        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [phase]);

    // Re-park mascot on window resize while parked/touring so he doesn't
    // drift off-screen. Also handles the corner-standby phases so Splash
    // stays glued to the bottom-right regardless of viewport size.
    useEffect(() => {
        function onResize() {
            // Corner phases always re-clamp to bottom-right.
            if (phase === 'skipped' || phase === 'skipped_open') {
                const { w: MW, h: MH } = mascotDims();
                setPosition({ x: getVW() - MW - 24, y: getVH() - MH - 24 });
                return;
            }
            if (phase !== 'parked' && phase !== 'touring' && phase !== 'done') return;
            const stop = stopIndex >= 0 ? TOUR_STOPS[stopIndex] : null;
            const anchorId = stop ? stop.anchorId : 'mascot-anchor-hero';
            const align = stop ? stop.align : 'right';
            const rect = getRect(anchorId);
            if (!rect) return;
            setPosition(parkPositionForAnchor(rect, align, getVW(), getVH()));
        }
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [phase, stopIndex]);

    // ----- render ---------------------------------------------------------
    if (!enabled) return null;

    // Corner phases: Splash parked in the bottom-right, fully visible.
    // 'skipped'       — idle in corner, no popup
    // 'skipped_open'  — popup showing (Start Tour button + close-outside)
    const inCornerIdle = phase === 'skipped';
    const inCornerOpen = phase === 'skipped_open';
    const inCorner = inCornerIdle || inCornerOpen;
    const flipStyle = facing === 'left' ? 'scaleX(-1)' : 'scaleX(1)';
    // Y-axis idle bob while parked OR while chilling in the corner.
    const bobbing = phase === 'parked' || phase === 'done' || inCorner;
    // Which sprite is showing right now, keyed off the pose state.
    const spriteMap = {
        stand: '/mascot/splash-hero.png',
        fly: '/mascot/splash-flying.png',
        point: '/mascot/splash-pointing.png',
        wave: '/mascot/splash-waving.png',
    };
    const currentSprite = spriteMap[pose] || spriteMap.stand;
    // Phase 0br: ground shadow is now rendered by <MascotFX /> canvas overlay
    // (dynamic shadow projected from a fixed light source, follows any
    // motion via DOM rect polling). Old `altitude` constant deleted.

    // Z-index policy (root container):
    //  * behindLogo=true  → z-[20]: brief "flies behind the S" moment during
    //                       the intro orbit. This is the ONLY place Splash
    //                       should ever slip behind another element.
    //  * touring / flying → z-[9999]: he's the tour guide, so he must sit on
    //                       top of EVERYTHING (including Leaflet-style maps
    //                       which internally stack up to z-index ~700).
    //  * everything else  → z-[70]: comfortably above landing content, below
    //                       page-level modals/toasts.
    const activePhase = phase;
    const rootZClass = behindLogo
        ? 'z-[20]'
        : (activePhase === 'touring' || activePhase === 'flyin' || activePhase === 'zoom' || activePhase === 'orbit'
            ? 'z-[9999]'
            : 'z-[70]');

    return (
        <div
            aria-live="polite"
            aria-label={t42("Splash the mascot")}
            data-testid="splash-mascot-root"
            data-behind-logo={behindLogo ? 'true' : 'false'}
            data-phase={activePhase}
            data-stop-id={phase === 'done' ? 'outro' : (TOUR_STOPS[stopIndex]?.anchorId || 'intro')}
            className={`pointer-events-none fixed inset-0 ${rootZClass}`}
            style={{
                transform: `translate(${shake.x}px, ${shake.y}px)`,
                transition: 'transform 30ms linear',
            }}
        >
            {/* Phase 0br: MascotFX canvas overlay — volumetric smoke +
                dynamic light-projected shadow that track the mascot's
                real DOM rect every frame. Zero React re-renders. */}
            <MascotFX targetSelector='[data-testid="splash-mascot-figure"]' />

            {/* Landing water-splash bursts. Rendered as siblings of the
                mascot so they persist through his next flight. Each cleans
                itself up via the onDone callback. */}
            {splashes.map((s) => (
                <WaterSplash
                    key={s.id}
                    x={s.x}
                    y={s.y}
                    small={s.small}
                    onDone={() => setSplashes((prev) => prev.filter((p) => p.id !== s.id))}
                />
            ))}

            {/* Sparkle / pixie-dust particles. High z, absolute-positioned,
                each animates outward and fades. Cheap DOM particles because
                there are typically fewer than 30 on screen at once. */}
            <AnimatePresence>
                {sparkles.map((s) => (
                    <motion.div
                        key={s.id}
                        aria-hidden
                        className="pointer-events-none absolute rounded-full"
                        style={{
                            left: s.x - s.size / 2,
                            top: s.y - s.size / 2,
                            width: s.size,
                            height: s.size,
                            background: `radial-gradient(circle, ${s.color} 0%, ${s.color}00 70%)`,
                            filter: 'blur(0.5px)',
                        }}
                        initial={{ opacity: 0.95, scale: 0.7, x: 0, y: 0 }}
                        animate={{
                            opacity: [0.95, 0.7, 0],
                            scale: [0.7, 1.1, 0.4],
                            x: s.dx,
                            y: [0, s.dy - 8, s.dy + 25],
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: s.life / 1000, ease: 'easeOut', times: [0, 0.4, 1] }}
                    />
                ))}
            </AnimatePresence>

            {/* Motion-blur trailing ghost images were replaced with the
                <MascotFX /> canvas overlay mounted at the root of this
                component (see below). */}

            {/* Mascot image \u2014 fixed-position, springs between positions. */}
            <motion.div
                data-testid="splash-mascot-figure"
                data-phase={phase}
                data-pose={pose}
                onClick={inCornerIdle ? handleReopen : undefined}
                role={inCornerIdle ? 'button' : undefined}
                aria-label={inCornerIdle ? t42("Open Splash tour") : undefined}
                tabIndex={inCornerIdle ? 0 : undefined}
                onKeyDown={inCornerIdle ? ((e) => {
                    // Keyboard access: Enter or Space opens the tour popup.
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleReopen();
                    }
                }) : undefined}
                className={`absolute pointer-events-auto ${inCornerIdle ? 'cursor-pointer' : ''}`}
                initial={false}
                animate={flightPath
                    ? {
                        x: flightPath.x,
                        y: flightPath.y,
                        // Phase 0br: play baked per-sample rotation + scale
                        // tracks if the arc builder supplied them, else fall
                        // back to the state-level tilt/scale (parked poses).
                        rotate: flightPath.rot || tilt,
                        scale: flightPath.s || scale,
                    }
                    : {
                        x: position.x,
                        y: position.y,
                        rotate: tilt,
                        scale,
                    }
                }
                transition={flightPath
                    ? {
                        // Easing is BAKED INTO the sample spacing (see
                        // buildArcPath / orbit builder above), so playback
                        // is linear — otherwise Framer would double-ease it.
                        x: { duration: flightPath.duration, ease: 'linear' },
                        y: { duration: flightPath.duration, ease: 'linear' },
                        rotate: flightPath.rot
                            ? { duration: flightPath.duration, ease: 'linear' }
                            : { duration: 0.3, ease: 'easeOut' },
                        scale: flightPath.s
                            ? { duration: flightPath.duration, ease: 'linear' }
                            : { duration: 0.3, ease: 'easeOut' },
                    }
                    : {
                        type: 'spring',
                        // A quick, damped settle avoids repeated stop-and-bounce beats.
                        stiffness: phase === 'touring' ? TOUR_MOTION.settleStiffness : 70,
                        damping: phase === 'touring' ? TOUR_MOTION.settleDamping : 14,
                        mass: phase === 'touring' ? 0.7 : 0.9,
                    }
                }
                style={{ width: mascotDims().w, height: mascotDims().h, top: 0, left: 0 }}
            >
                {/* Ground shadow underneath \u2014 shrinks while airborne. */}
                {/* Phase 0br: ground shadow removed — now rendered by <MascotFX /> canvas overlay (light-projected, follows real motion). */}

                {/* Ambient swim bubbles rising around him. Only spawned
                    while parked/speaking so we don't distract during the
                    high-motion flight sequences. */}
                {(phase === 'parked' || phase === 'done' || (phase === 'touring' && showBubble)) && (
                    <AmbientBubbles
                        anchorX={mascotDims().w / 2}
                        anchorY={mascotDims().h - 20}
                        count={isMobileViewport() ? 4 : 6}
                        mobile={isMobileViewport()}
                    />
                )}

                {/* Sprite crossfade \u2014 when pose changes we soft-swap so the
                    transition never looks like a hard cut. */}
                <motion.div
                    className="absolute inset-0"
                    style={{ transformOrigin: 'center bottom', willChange: 'transform' }}
                    animate={{ scaleX: stretchX, scaleY: stretchY, y: headBob }}
                    transition={{
                        scaleX: { type: 'spring', stiffness: 310, damping: 23, mass: 0.7 },
                        scaleY: { type: 'spring', stiffness: 310, damping: 23, mass: 0.7 },
                        y: { duration: 0.1, ease: 'linear' },
                    }}
                >
                    <div className="absolute inset-0" style={{ transform: flipStyle }}>
                    <AnimatePresence mode="popLayout" initial={false}>
                    <motion.img
                        key={currentSprite}
                        src={currentSprite}
                        alt="Splash the SplashPad hero mascot"
                        draggable={false}
                        className="absolute inset-0 h-full w-full select-none drop-shadow-[0_18px_30px_rgba(10,22,40,0.35)]"
                        initial={{ opacity: 0 }}
                        animate={
                            bobbing
                                ? { opacity: 1, y: [0, -8, 0] }
                                : { opacity: 1, y: 0 }
                        }
                        exit={{ opacity: 0 }}
                        transition={
                            bobbing
                                ? { opacity: { duration: 0.18 }, y: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' } }
                                : { opacity: { duration: 0.18 }, y: { duration: 0.2 } }
                        }
                    />
                </AnimatePresence>
                </div>
                </motion.div>

                {/* Phase 0br: cyan speed-lines gradient removed. Motion trail is now the MascotFX canvas overlay. */}
            </motion.div>

            {/* Speech bubble \u2014 offset to the left/right of the mascot depending
                on which side he's facing. */}
            <AnimatePresence>
                {showBubble && (
                    <motion.div
                        key="splash-tour-bubble"
                        data-testid="splash-mascot-bubble"
                        initial={{ opacity: 0, scale: 0.9, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -8 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="pointer-events-auto absolute"
                        style={{
                            // Bubble hovers above the mascot's current position.
                            // On mobile, we anchor it to the viewport left edge
                            // with generous padding so it never overflows.
                            left: isMobileViewport()
                                ? 12
                                : Math.max(16, Math.min(position.x - 140, getVW() - 360)),
                            top: Math.max(72, position.y - (isMobileViewport() ? 110 : 130)),
                            maxWidth: isMobileViewport() ? getVW() - 24 : 340,
                            width: isMobileViewport() ? getVW() - 24 : undefined,
                        }}
                    >
                        <div className="relative rounded-2xl border border-[#0a1628]/10 bg-white/95 px-5 py-4 shadow-[0_18px_40px_rgba(10,22,40,0.22)] backdrop-blur">
                            <button
                                type="button"
                                onClick={handleSkip}
                                aria-label={t42("Close Splash tour")}
                                data-testid="splash-mascot-close"
                                className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-white text-[#0a1628] shadow ring-1 ring-[#0a1628]/10 hover:bg-[#f4f7fb]"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                            <p className="text-sm leading-snug text-[#0a1628]" data-testid="splash-mascot-line">
                                {bubbleText}
                            </p>
                            {audioError && <p className="mt-2 text-xs text-[color:var(--sp-text-muted)]" data-testid="splash-mascot-audio-error-hint">{t42("Audio unavailable — tour continues with text.")}</p>}
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                {(phase === 'parked' || phase === 'skipped_open') && stopIndex < 0 && (
                                    <button
                                        type="button"
                                        onClick={handleBubbleCta}
                                        data-testid="splash-mascot-start-tour"
                                        className="inline-flex items-center gap-1 rounded-full bg-[#0a1628] px-4 py-1.5 text-xs font-semibold text-white shadow hover:bg-[#0a1628]/90"
                                    >
                                        <Play className="h-3.5 w-3.5" /> {t42("Start Tour")} </button>
                                )}
                                {phase === 'touring' && bubbleCta && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-[#00d4ff]/15 px-3 py-1 text-[11px] font-semibold text-[#0a1628]">
                                        <ChevronDown className="h-3 w-3" /> {bubbleCta}
                                    </span>
                                )}
                                {phase === 'done' && (
                                    <button
                                        type="button"
                                        onClick={handleBubbleCta}
                                        data-testid="splash-mascot-cta-final"
                                        className="inline-flex items-center gap-1 rounded-full bg-[#ffd700] px-4 py-1.5 text-xs font-semibold text-[#0a1628] hover:bg-[#ffd700]/90"
                                    >
                                        {bubbleCta}
                                    </button>
                                )}
                                {(phase === 'parked' || phase === 'touring' || phase === 'done' || phase === 'skipped_open') && (
                                    <button
                                        type="button"
                                        onClick={toggleMute}
                                        aria-label={muted ? t42("Unmute Splash") : t42("Mute Splash")}
                                        data-testid="splash-mascot-mute"
                                        className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#0a1628]/15 bg-white px-2.5 py-1 text-[11px] text-[#1a3a5c] hover:bg-[#f4f7fb]"
                                    >
                                        {muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                                        {muted ? t42("Muted") : t42("Voice on")}
                                    </button>
                                )}
                                {(phase === 'touring' || phase === 'done') && (
                                    <button
                                        type="button"
                                        onClick={handleSkip}
                                        data-testid="splash-mascot-skip"
                                        className="inline-flex items-center gap-1 rounded-full border border-[#0a1628]/15 bg-white px-2.5 py-1 text-[11px] text-[#1a3a5c] hover:bg-[#f4f7fb]"
                                    >
                                        <SkipForward className="h-3 w-3" /> {t42("Skip")} </button>
                                )}
                            </div>
                            {/* Speech-bubble tail pointing toward the mascot. */}
                            <div
                                aria-hidden
                                className="absolute h-4 w-4 rotate-45 border-b border-r border-[#0a1628]/10 bg-white/95"
                                style={{
                                    left: Math.min(280, Math.max(16, position.x - Math.max(16, position.x - 140))),
                                    bottom: -8,
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Corner-standby (Bug 2 fix, 2026-07):
                The old design here rendered a separate ~40px "Meet Splash"
                mini pill when the user dismissed the tour. That mini pill
                lived alongside a scaled-down (0.5x) main figure that was
                cropped off the right edge of the viewport — which is what
                the user described as "you can only kind of see his head
                and his hand."

                The new design uses the MAIN mascot figure itself as the
                corner-standby element (see the useEffect above that snaps
                him to `(vw - MW - 24, vh - MH - 24)` at scale=1 whenever
                phase is 'skipped' or 'skipped_open'). The figure gets a
                `cursor-pointer` and an `onClick={handleReopen}` in that
                mode, plus keyboard access. So there is nothing to render
                here anymore. Comment retained as a "why" breadcrumb for
                future maintainers. */}
        </div>
    );
}

function wait(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

// -----------------------------------------------------------------------------
// <AmbientBubbles /> — swim-themed atmosphere
// -----------------------------------------------------------------------------
// Continuously rising water bubbles around the mascot's vicinity. Purely
// decorative but sells the "swim" brand while Splash is on screen. Each
// bubble is a translucent circle with a highlight rim that rises, drifts
// slightly sideways, and pops at the top.
//
// We render N bubbles at all times and cycle through their lifecycles by
// giving each a unique random offset. Framer Motion runs the entire loop
// declaratively — the component performs no per-frame work.
// -----------------------------------------------------------------------------
function AmbientBubbles({ anchorX, anchorY, count = 6, mobile = false }) {
    // Fixed random-ish offsets so bubbles are staggered rather than in sync.
    const bubbles = React.useMemo(() => {
        const arr = [];
        for (let i = 0; i < count; i++) {
            arr.push({
                id: i,
                size: 8 + Math.random() * 14,
                offsetX: (Math.random() - 0.5) * (mobile ? 80 : 160),
                riseDist: 100 + Math.random() * 100,
                driftX: (Math.random() - 0.5) * 40,
                delay: (i / count) * 4.2 + Math.random() * 0.5,
                duration: 4 + Math.random() * 2,
            });
        }
        return arr;
    }, [count, mobile]);

    return (
        <div
            aria-hidden
            className="pointer-events-none absolute"
            style={{ left: anchorX, top: anchorY, width: 1, height: 1 }}
        >
            {bubbles.map((b) => (
                <motion.div
                    key={b.id}
                    className="absolute rounded-full"
                    style={{
                        left: b.offsetX,
                        top: 0,
                        width: b.size,
                        height: b.size,
                        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, rgba(0,212,255,0.35) 40%, rgba(0,180,220,0.15) 100%)',
                        boxShadow: 'inset 0 0 6px rgba(255,255,255,0.4)',
                        border: '1px solid rgba(255,255,255,0.3)',
                    }}
                    initial={{ opacity: 0, y: 0, scale: 0.4 }}
                    animate={{
                        opacity: [0, 0.85, 0.85, 0],
                        y: [0, -b.riseDist * 0.6, -b.riseDist],
                        x: [0, b.driftX * 0.5, b.driftX],
                        scale: [0.4, 1, 0.9],
                    }}
                    transition={{
                        duration: b.duration,
                        delay: b.delay,
                        repeat: Infinity,
                        repeatType: 'loop',
                        ease: 'easeOut',
                        times: [0, 0.15, 0.85, 1],
                    }}
                />
            ))}
        </div>
    );
}

// -----------------------------------------------------------------------------
// <WaterSplash /> — landing water-splash burst
// -----------------------------------------------------------------------------
// When Splash lands from a flight, we drop one of these at his feet. Eight
// water droplets radiate outward on a cubic-bezier "explode then fall" curve
// while a soft ripple ring expands and fades. The whole burst self-destructs
// after ~700ms via the onAnimationComplete callback wired up in the parent.
//
// Fits the swim brand perfectly and provides the visceral "landing thump"
// signal every Disney animator uses to sell weight and impact.
// -----------------------------------------------------------------------------
function WaterSplash({ x, y, onDone, small = false }) {
    const droplets = 8;
    const R = small ? 30 : 55;
    return (
        <motion.div
            className="pointer-events-none absolute"
            style={{ left: x, top: y }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: 'easeIn' }}
            onAnimationComplete={onDone}
            aria-hidden
        >
            {/* Expanding ripple ring */}
            <motion.div
                className="absolute rounded-full border-2 border-[#00d4ff]/70"
                style={{ left: -R, top: -R * 0.35, width: R * 2, height: R * 0.7 }}
                initial={{ scale: 0.3, opacity: 0.9 }}
                animate={{ scale: 1.4, opacity: 0 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
            />
            {/* Radiating droplets */}
            {Array.from({ length: droplets }).map((_, i) => {
                // Half-circle upward burst (angles from -160° to -20°)
                const a = -Math.PI + (i / (droplets - 1)) * Math.PI;
                const dx = Math.cos(a) * R * 1.1;
                const dy = Math.sin(a) * R * 0.9 - 4;
                const size = small ? 6 : 8 + Math.random() * 5;
                return (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-gradient-to-b from-[#00d4ff] to-[#0a95c9]"
                        style={{ width: size, height: size, left: -size / 2, top: -size / 2 }}
                        initial={{ x: 0, y: 0, opacity: 0.95, scale: 1 }}
                        animate={{
                            x: dx,
                            y: [0, dy, dy + R * 0.9],
                            opacity: [0.95, 0.85, 0],
                            scale: [1, 0.9, 0.5],
                        }}
                        transition={{ duration: 0.65, ease: [0.34, 1.2, 0.64, 1], times: [0, 0.55, 1] }}
                    />
                );
            })}
        </motion.div>
    );
}

// Phase 0br: <GroundShadow /> deleted — shadow is now drawn on the
// <MascotFX /> canvas (dynamic, light-projected, follows real motion).

export default SplashHeroMascot;
